import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CircleOff } from "lucide-react";

import { isAppEnabled } from "../lib/appAccess";
import { useEnabledApps } from "../hooks/useEnabledApps";

/**
 * Route-level org app-access gate (companion to ModuleAccessGuard's RBAC
 * gate). Blocks direct navigation to apps disabled for the user's
 * organization (Per-org app access screen) or globally (App Registry); the
 * sidebar filtering in main.tsx hides the same apps from nav. Fails open
 * while enablement is loading/unknown and for routes that aren't
 * org-toggleable. Applies to super admins too — the Admin module itself is
 * never gated, so re-enabling stays reachable.
 */
export function AppAccessGuard({
  routeId,
  children,
}: {
  routeId: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const enabledAppIds = useEnabledApps();
  if (isAppEnabled(enabledAppIds, routeId)) {
    return <>{children}</>;
  }
  return (
    <div className="flex-1 flex items-center justify-center p-8" role="alert">
      <div className="max-w-md text-center space-y-3">
        <CircleOff className="mx-auto h-10 w-10 opacity-40" aria-hidden />
        <h1 className="text-xl font-semibold">{t("shell.appDisabled.title")}</h1>
        <p className="text-sm opacity-70">{t("shell.appDisabled.body")}</p>
      </div>
    </div>
  );
}
